import { noChange, type ElementPart } from 'lit'
import { AsyncDirective, directive } from 'lit/async-directive.js'
import { PartType } from 'lit/directive.js'
import { reducedMotion$ } from './reduced-motion'

export interface CurtainOptions {
	/** Whether the curtain is drawn over the host. Toggling to `false` drains it away. Default: `true` */
	active?: boolean
	/** Gravity-drain duration in ms. Default: `720` */
	drainMs?: number
}

/**
 * A curtain MUTES, it does not obscure — the host's own content stays crisp and
 * legible beneath it. No blur (detail is preserved); the mute is desaturation +
 * a dim + a veil tint, tuned to be subtle yet clearly visible against a crisp
 * neighbour.
 */
const MUTE = { saturate: 0.58, brightness: 0.8, tint: 0.22 }

const DEFAULTS: Required<CurtainOptions> = { active: true, drainMs: 720 }

/**
 * Toggling `active` to `false` drains the curtain down and out under gravity
 * then removes it; under `prefers-reduced-motion` it is removed instantly.
 *
 * The overlay is `pointer-events: none`, so the host stays interactive — place
 * any action (an Add button) as a sibling with a higher z-index to render it
 * crisp on top of the frost.
 */
class CurtainDirective extends AsyncDirective {
	private element!: HTMLElement
	private overlayEl?: HTMLDivElement
	private didSetPosition = false
	private origOverflow = ''
	private draining = false
	private drainTimeout?: ReturnType<typeof setTimeout>
	private currentActive = DEFAULTS.active
	private currentDrainMs = DEFAULTS.drainMs

	render(_options?: CurtainOptions) {
		void _options
		return noChange
	}

	override update(part: ElementPart, [options]: [CurtainOptions?]) {
		if (part.type !== PartType.ELEMENT) {
			throw new Error('curtain directive must be used on an element')
		}

		this.element = part.element as HTMLElement
		this.currentActive = options?.active ?? DEFAULTS.active
		this.currentDrainMs = options?.drainMs ?? DEFAULTS.drainMs

		if (this.currentActive) this.draw()
		else this.drain()

		return noChange
	}

	override reconnected() {
		if (this.currentActive) this.draw()
	}

	private draw() {
		if (!this.isConnected) return

		if (this.drainTimeout) {
			clearTimeout(this.drainTimeout)
			this.drainTimeout = undefined
		}
		this.draining = false

		if (getComputedStyle(this.element).position === 'static') {
			this.element.style.position = 'relative'
			this.didSetPosition = true
		}

		if (!this.overlayEl) {
			this.origOverflow = this.element.style.overflow
			this.element.style.overflow = 'hidden'
			const overlay = document.createElement('div')
			overlay.setAttribute('aria-hidden', 'true')
			this.element.appendChild(overlay)
			this.overlayEl = overlay
		}

		const frost = `saturate(${MUTE.saturate}) brightness(${MUTE.brightness})`
		Object.assign(this.overlayEl.style, {
			position: 'absolute',
			inset: '0',
			pointerEvents: 'none',
			backdropFilter: frost,
			background: `rgba(var(--md-sys-color-surface-container, 28 28 28) / ${MUTE.tint})`,
			boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.10)',
			willChange: 'transform',
			transition: '',
			transform: 'translateY(0)',
			opacity: '1',
		})
		this.overlayEl.style.setProperty('-webkit-backdrop-filter', frost)
	}

	private drain() {
		const overlay = this.overlayEl
		if (!overlay || this.draining) return
		this.draining = true

		if (reducedMotion$.value) {
			this.teardown()
			return
		}

		// Ease-in fall = gravity; slides fully out the bottom, fading as it goes.
		overlay.style.transition = `transform ${this.currentDrainMs}ms cubic-bezier(0.4,0,0.85,0.12), opacity ${this.currentDrainMs}ms ease`
		overlay.style.transform = 'translateY(101%)'
		overlay.style.opacity = '0'
		this.drainTimeout = setTimeout(() => {
			this.drainTimeout = undefined
			this.teardown()
		}, this.currentDrainMs)
	}

	private teardown() {
		this.overlayEl?.remove()
		this.overlayEl = undefined
		this.draining = false
		if (this.didSetPosition) {
			this.element.style.position = ''
			this.didSetPosition = false
		}
		this.element.style.overflow = this.origOverflow
	}

	override disconnected() {
		if (this.drainTimeout) {
			clearTimeout(this.drainTimeout)
			this.drainTimeout = undefined
		}
		this.teardown()
	}
}

export const curtain = directive(CurtainDirective)
